import { TOKEN_EXPIRY } from "@/configs";
import { ensureSuperTokensInitialized } from "@/lib/auth/supertokensInit";
import { logger } from "@/lib/logger";
import { getCookie, setCookie } from "@/utils/session";
import Session from "supertokens-auth-react/recipe/session";

const DEFAULT_REFRESH_WINDOW_MS = 60_000; // 1 minute
const FAILED_REFRESH_SUPPRESSION_WINDOW_MS = 5 * 60_000; // 5 minutes

export type SessionRefreshSource = "Authenticated" | "_app fromSupertokens" | "other";
export type SessionExpirySource = "cookie" | "secure_payload" | "missing";

export type SessionExpiryDetails = {
  expiryMs: number | null;
  source: SessionExpirySource;
  rawExpiryMs?: number;
  msUntilExpiry?: number;
};

let refreshInFlightPromise: Promise<boolean> | null = null;
let proactiveRefreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
let refreshFailureSuppressedUntilMs = 0;

function clearScheduledProactiveRefresh() {
  if (proactiveRefreshTimeoutId) {
    clearTimeout(proactiveRefreshTimeoutId);
    proactiveRefreshTimeoutId = null;
  }
}

function suppressRefreshRetries() {
  refreshFailureSuppressedUntilMs = Date.now() + FAILED_REFRESH_SUPPRESSION_WINDOW_MS;
}

function shouldSuppressRefreshRetries() {
  return refreshFailureSuppressedUntilMs > Date.now();
}

function getExpiryMsFromPayload(payload: unknown) {
  const rawExpiry = Number((payload as { exp?: unknown } | null | undefined)?.exp);
  if (!Number.isFinite(rawExpiry) || rawExpiry <= 0) {
    return null;
  }

  return rawExpiry > 1_000_000_000_000 ? rawExpiry : rawExpiry * 1000;
}

async function syncTokenExpiryCookieFromSession() {
  try {
    ensureSuperTokensInitialized();
    const payload = await Session.getAccessTokenPayloadSecurely();
    const expiryMs = getExpiryMsFromPayload(payload);
    if (!expiryMs) {
      return;
    }

    setCookie(TOKEN_EXPIRY, String(expiryMs));
  } catch (err) {
    logger.error("sessionRefresh: failed to sync token expiry cookie from secure session", { err });
  }
}

function getExpiryMsFromCookie() {
  const tokenExpiryCookie = Number(getCookie(TOKEN_EXPIRY) || "0");
  if (Number.isFinite(tokenExpiryCookie) && tokenExpiryCookie > 0) {
    return tokenExpiryCookie;
  }

  return null;
}

export async function getSessionExpiryDetails(): Promise<SessionExpiryDetails> {
  const nowMs = Date.now();
  const tokenExpiryCookie = getExpiryMsFromCookie();
  if (tokenExpiryCookie) {
    const msUntilExpiry = tokenExpiryCookie - nowMs;
    logger.debug("sessionRefresh: using expiry from cookie", {
      expiryMs: tokenExpiryCookie,
      msUntilExpiry
    });
    return {
      expiryMs: tokenExpiryCookie,
      source: "cookie",
      msUntilExpiry
    };
  }

  logger.debug("sessionRefresh: expiry cookie missing, reading secure payload");

  try {
    ensureSuperTokensInitialized();
    const payload = await Session.getAccessTokenPayloadSecurely();
    const rawExpiry = Number((payload as { exp?: unknown } | null | undefined)?.exp);
    const payloadExpiryMs = getExpiryMsFromPayload(payload);
    if (payloadExpiryMs) {
      const msUntilExpiry = payloadExpiryMs - nowMs;
      logger.debug("sessionRefresh: using expiry from secure payload", {
        rawExpiry,
        expiryMs: payloadExpiryMs,
        msUntilExpiry
      });
      return {
        expiryMs: payloadExpiryMs,
        source: "secure_payload",
        rawExpiryMs: rawExpiry,
        msUntilExpiry
      };
    }

    logger.debug("sessionRefresh: secure payload had no usable expiry", {
      hasExpField: Number.isFinite(rawExpiry) && rawExpiry > 0
    });
  } catch (err) {
    logger.error("sessionRefresh: failed to read secure session expiry", { err });
  }

  return {
    expiryMs: null,
    source: "missing"
  };
}

export async function attemptSessionRefresh(source: SessionRefreshSource = "other"): Promise<boolean> {
  if (refreshInFlightPromise) return refreshInFlightPromise;
  if (shouldSuppressRefreshRetries()) {
    return false;
  }

  refreshInFlightPromise = (async () => {
    try {
      logger.debug("sessionRefresh: attemptSessionRefresh", { source });
      ensureSuperTokensInitialized();
      const refreshed = await Session.attemptRefreshingSession();
      if (!refreshed) {
        suppressRefreshRetries();
        return false;
      }

      refreshFailureSuppressedUntilMs = 0;
      await syncTokenExpiryCookieFromSession();
      return true;
    } catch (err) {
      logger.error("sessionRefresh: attemptRefreshingSession failed", { err });
      suppressRefreshRetries();
      return false;
    } finally {
      refreshInFlightPromise = null;
    }
  })();

  return refreshInFlightPromise;
}

export async function getSessionExpiryMs(): Promise<number | null> {
  const details = await getSessionExpiryDetails();
  return details.expiryMs;
}

export function cancelScheduledSessionRefresh() {
  clearScheduledProactiveRefresh();
}

export function clearSessionRefreshFailureSuppression() {
  refreshFailureSuppressedUntilMs = 0;
}

export function scheduleProactiveSessionRefresh(
  params: { tokenExpiryMs: number; refreshWindowMs?: number },
  opts?: { skipImmediateIfDelayNonPositive?: boolean; source?: SessionRefreshSource }
): () => void {
  const refreshWindowMs = params.refreshWindowMs ?? DEFAULT_REFRESH_WINDOW_MS;
  clearScheduledProactiveRefresh();

  if (!Number.isFinite(params.tokenExpiryMs) || params.tokenExpiryMs <= 0) {
    return () => {};
  }

  const delayMs = params.tokenExpiryMs - refreshWindowMs - Date.now();
  if (delayMs <= 0) {
    if (!opts?.skipImmediateIfDelayNonPositive) {
      void attemptSessionRefresh(opts?.source ?? "other");
    }
    return () => {};
  }

  const timeoutId = setTimeout(() => {
    if (proactiveRefreshTimeoutId === timeoutId) proactiveRefreshTimeoutId = null;
    proactiveRefreshTimeoutId = null;
    void attemptSessionRefresh(opts?.source ?? "other");
  }, delayMs);

  proactiveRefreshTimeoutId = timeoutId;

  return () => {
    if (proactiveRefreshTimeoutId === timeoutId) {
      clearTimeout(timeoutId);
      proactiveRefreshTimeoutId = null;
    }
  };
}
